import Icon from "~/components/ui/Icon"
import Slider from "~/components/ui/Slider"
import SliderJS from "~/components/ui/SliderJS"
import ThumbnailSlider from "~/components/product/ProductDetails/ThumbnailSlider"
import ZoomableImage from "~/components/product/ProductDetails/ZoomableImage"
import { useId } from "react"
import { ImageObject } from "@decocms/apps-commerce/types"
import Image from "~/components/ui/Image"

export interface Props {
    images: ImageObject[]
    width: number
    height: number
    productURL?: string
    isMobile?: boolean
    productName: string
}

export default function ImageSlider(
    { images, width, height, productURL, isMobile, productName }: Props,
) {
    const id = useId()

    // Products with name empty come first
    images.sort((a, b) => {
        if (!a.name && !b.name) {
            return 0
        }

        if (!a.name) {
            return -1
        }

        if (!b.name) {
            return 1
        }

        return 0
    })

    return (
        <>
            <div
                id={id}
                className='grid grid-cols-1 grid-rows-[1fr_auto] select-none sm:grid-cols-[auto_1fr] sm:grid-rows-1 lg:self-start lg:sticky lg:top-4 lg:!z-10'
            >
                {/* Image Slider */}
                <div className='relative order-1 sm:order-2 h-fit'>
                    <div className='absolute left-0 top-4 lg:top-[55px] z-[1]'>
                        <input type='checkbox' id='share-button' className='hidden peer' />
                        <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${productURL}`}
                            className='absolute top-0 flex items-center justify-center transition-all bg-white rounded-full peer-checked:top-[-55px] text-neutral-400 hover:bg-pink hover:text-white left-[6px] w-[35px] h-[35px]'
                            aria-label='Compartilhar no Facebook'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <Icon id='FacebookFilled' size={28} />
                        </a>
                        <a
                            href={`https://twitter.com/share?url=${productURL}`}
                            className='absolute top-0 flex items-center justify-center transition-all bg-white rounded-full peer-checked:top-[-31px] peer-checked:left-[55px] text-neutral-400 hover:bg-pink hover:text-white left-[5px] w-[35px] h-[35px]'
                            aria-label='Compartilhar no Twitter'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <Icon id='X' size={24} />
                        </a>
                        <a
                            href={`http://pinterest.com/pin/create/link/?url=${images[0]
                                .url!}`}
                            className='absolute top-0 flex items-center justify-center transition-all bg-white rounded-full peer-checked:top-[41px] peer-checked:left-[55px] text-neutral-400 hover:bg-pink hover:text-white left-[5px] w-[35px] h-[35px]'
                            aria-label='Compartilhar no Pinterest'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <Icon id='PinterestFilled' size={28} />
                        </a>
                        <a
                            href={`https://api.whatsapp.com/send?phone=&text=${productURL}`}
                            className='absolute top-0 flex items-center justify-center transition-all bg-white rounded-full peer-checked:top-[70px] text-neutral-400 hover:bg-pink hover:text-white left-[6px] w-[35px] h-[35px]'
                            aria-label='Compartilhar no Whatsapp'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <Icon id='WhatsappFilled' size={28} />
                        </a>
                        <label
                            htmlFor='share-button'
                            className='absolute top-0 left-0 flex items-center justify-center transition-all bg-white rounded-full shadow-lg cursor-pointer w-[50px] h-[50px] hover:bg-red-wine text-neutral-400 hover:text-white peer-checked:bg-red-wine peer-checked:text-white'
                        >
                            <Icon id='Share' size={30} strokeWidth={3} role='img' aria-label='Compartilhar' />
                        </label>
                    </div>
                    <Slider className='gap-6 carousel carousel-center w-full'>
                        {images.map((img, index) => (
                            <Slider.Item
                                key={img.url!}
                                index={index}
                                className='w-full carousel-item flex justify-center items-center'
                            >
                                {isMobile
                                    ? (
                                        <Image
                                            className='w-full'
                                            sizes='(max-width: 640px) 100vw, 40vw'
                                            src={img.url!}
                                            alt={productName}
                                            width={width}
                                            height={height}
                                            // Preload LCP image for better web vitals
                                            preload={index === 0}
                                            loading={index === 0 ? 'eager' : 'lazy'}
                                            fetchPriority={index === 0 ? 'high' : 'auto'}
                                        />
                                    )
                                    : (
                                        <ZoomableImage
                                            className='w-full'
                                            sizes='(max-width: 640px) 100vw, 40vw'
                                            src={img.url!}
                                            alt={productName}
                                            width={width}
                                            height={height}
                                            // Preload LCP image for better web vitals
                                            preload={index === 0}
                                            loading={index === 0 ? 'eager' : 'lazy'}
                                            fetchPriority={index === 0 ? 'high' : 'auto'}
                                        />
                                    )}
                            </Slider.Item>
                        ))}
                    </Slider>

                    {images.length > 1 && (
                        <>
                            <Slider.PrevButton
                                className='absolute bg-transparent hover:bg-transparent disabled:bg-transparent border-none shadow-none no-animation left-2 !-translate-y-1/2 top-1/2 btn btn-ghost'
                                disabled
                            >
                                <Icon size={24} id='ChevronLeft' strokeWidth={2} />
                            </Slider.PrevButton>

                            <Slider.NextButton
                                className='absolute bg-transparent hover:bg-transparent disabled:bg-transparent border-none shadow-none no-animation right-2 !-translate-y-1/2 top-1/2 btn btn-ghost'
                                disabled={images.length < 2}
                            >
                                <Icon size={24} id='ChevronRight' strokeWidth={2} />
                            </Slider.NextButton>
                        </>
                    )}
                </div>

                {/* Dots */}
                <div className='relative order-2 sm:order-1 flex flex-col items-center w-full sm:w-auto'>
                    <div
                        data-thumb-arrow='prev'
                        className='hidden sm:flex shrink-0 mb-1 invisible pointer-events-none'
                    >
                        <button
                            type='button'
                            className='bg-transparent hover:bg-transparent disabled:bg-transparent border-none shadow-none no-animation btn btn-ghost p-0 min-h-0 h-auto'
                            aria-label='Imagem anterior'
                            disabled
                        >
                            <Icon id='ChevronUp' size={24} strokeWidth={2} />
                        </button>
                    </div>

                    <ul
                        data-thumbnails
                        className='carousel carousel-center sm:flex-col gap-4 overflow-x-auto sm:overflow-y-auto sm:overflow-x-hidden scrollbar-none w-full sm:max-h-[584px]'
                    >
                        {images.map((img, index) => (
                            <li key={img.url ?? index} className='carousel-item min-w-[63px] sm:min-w-[100px] shrink-0'>
                                <Slider.Dot index={index} className='cursor-pointer'>
                                    <Image
                                        className='border-2 border-gray-200 rounded opacity-70 group-disabled:opacity-100 transition-opacity group-disabled:border-salmon group-disabled:border-b-4 w-[84px] h-[84px] object-contain bg-white'
                                        width={84}
                                        height={84}
                                        src={img.url!}
                                        alt={productName}
                                        fetchPriority="low"
                                        loading="lazy"
                                        fit='contain'
                                    />
                                </Slider.Dot>
                            </li>
                        ))}
                    </ul>

                    <div
                        data-thumb-arrow='next'
                        className='hidden sm:flex shrink-0 mt-2 invisible pointer-events-none'
                    >
                        <button
                            type='button'
                            className='bg-transparent hover:bg-transparent disabled:bg-transparent border-none shadow-none no-animation btn btn-ghost p-0 min-h-0 h-auto'
                            aria-label='Próxima imagem'
                            disabled
                        >
                            <Icon id='ChevronDown' size={24} strokeWidth={2} />
                        </button>
                    </div>
                </div>

                <SliderJS rootId={id} />
                <ThumbnailSlider rootId={id} />
            </div>
        </>
    )
}
